import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, XIcon, WavesIcon, UsersIcon, BarChart3Icon, ShieldIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { LoisirsOpsDashboard } from './LoisirsOpsDashboard';
import { LoisirsDemoCard } from './LoisirsDemoCard';
type Bilingual = {
  en: string;
  fr: string;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  }
};
const LEGACY = ['Amilia', 'Ludik', 'Activitek', 'Sport-Plus', 'Coba'];
const BEFORE: Bilingual[] = [{
  fr: 'Données dispersées',
  en: 'Scattered data'
}, {
  fr: 'Saisies multiples',
  en: 'Duplicate data entry'
}, {
  fr: 'Processus manuels',
  en: 'Manual processes'
}, {
  fr: 'Outils d’administration séparés',
  en: 'Separate admin tools'
}];
const AFTER: Bilingual[] = [{
  fr: 'Une seule plateforme',
  en: 'One platform'
}, {
  fr: 'Une seule identité',
  en: 'One identity'
}, {
  fr: 'Une vue opérationnelle unique',
  en: 'One operational view'
}, {
  fr: 'L’intelligence ATNA intégrée',
  en: 'ATNA intelligence built in'
}];
const BENEFITS: {
  icon: typeof UsersIcon;
  lead: Bilingual;
  accent: Bilingual;
}[] = [{
  icon: UsersIcon,
  lead: {
    fr: 'Plus simple pour les',
    en: 'Simpler for'
  },
  accent: {
    fr: 'citoyens.',
    en: 'residents.'
  }
}, {
  icon: BarChart3Icon,
  lead: {
    fr: 'Plus de capacité pour',
    en: 'More capacity for'
  },
  accent: {
    fr: 'vos équipes.',
    en: 'your teams.'
  }
}, {
  icon: ShieldIcon,
  lead: {
    fr: 'Des opérations plus efficaces et',
    en: 'Operations that are more efficient and'
  },
  accent: {
    fr: 'durables.',
    en: 'sustainable.'
  }
}];
export function LoisirsReplace() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  return <div className="max-w-[1480px] mx-auto" data-id="element-4513" data-mp="3m1orjomavw5k">
      <div className="grid grid-cols-1 xl:grid-cols-[45fr_55fr] gap-12 xl:gap-10 items-start" data-id="element-4514" data-mp="3ccrlwmc97f60">
        {/* Left */}
        <motion.div {...reveal} transition={{
        duration: 0.5,
        ease: EASE
      }} data-id="element-4515" data-mp="3hdfbr4oqxpyn">
          <p className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#5B6478]" data-id="element-4516" data-mp="32fmykj6o9dnm">
            {isFR ? 'Passez à une plateforme de loisirs intelligente' : 'Move to an intelligent recreation platform'}
          </p>
          <h2 className="font-sans font-semibold text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-[-0.03em] text-[#121212] mt-4" data-id="element-4517" data-mp="3v53o992wqgg5">
            {isFR ? 'Remplacez la complexité' : 'Replace complexity'}{' '}
            <span className="text-[#4F46E5]" data-id="element-4518" data-mp="3ldpi4iggaie1">
              {isFR ? 'par une seule plateforme intelligente.' : 'with one intelligent platform.'}
            </span>
          </h2>
          <p className="font-sans text-lg text-[#5B6478] leading-relaxed mt-5 max-w-2xl" data-id="element-4519" data-mp="hwh1g1xglokw">
            {isFR ? 'Loisirs ATNA réunit inscriptions, camps, réservations, ressources, paiements, présences, accès, contrats et opérations dans une même plateforme — avec l’intelligence ATNA intégrée.' : 'ATNA Recreation brings registrations, camps, bookings, resources, payments, attendance, access, contracts and operations into one platform — with ATNA intelligence built in.'}
          </p>

          <div className="mt-8 flex flex-wrap gap-3" data-id="element-4520" data-mp="2c5mjbyde06ox">
            <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
            location: 'loisirs_replace',
            cta: 'replace_current_system'
          })} className="inline-flex items-center gap-2 rounded-xl bg-[#4F46E5] px-6 py-3.5 font-sans text-[15px] font-medium text-white hover:bg-[#4338CA] transition-colors duration-150" data-id="element-4521" data-mp="1of0a43rncgdc">
              {isFR ? 'Voir comment remplacer mon système actuel' : 'See how to replace my current system'}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-4522" data-mp="34ts4gm8crxpa" />
            </Link>
            <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
            location: 'loisirs_replace',
            cta: 'schedule_demo'
          })} className="inline-flex items-center rounded-xl border border-[#4F46E5] px-6 py-3.5 font-sans text-[15px] font-medium text-[#4F46E5] hover:bg-[#F4F4FE] transition-colors duration-150" data-id="element-4523" data-mp="34w4i4n72vlwj">
              {isFR ? 'Demander une démo' : 'Request a demo'}
            </Link>
          </div>
          <p className="font-sans text-[14px] text-[#5B6478] mt-3" data-id="element-4524" data-mp="mwwx1hiboyy0">
            {isFR ? 'Présentez-nous votre système actuel : nous vous montrerons comment migrer vers Loisirs ATNA.' : 'Show us your current system. We’ll show you how to migrate to ATNA Recreation.'}
          </p>

          {/* Before / migration / after */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch" data-id="element-4525" data-mp="15og0fpg9ef0k">
            <div className="rounded-2xl border border-[#EEF0F4] bg-[#FAFAFB] p-5" data-id="element-4526" data-mp="1amhiq85v2aaz">
              <p className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5B6478]" data-id="element-4527" data-mp="2v51tttvyohl0">
                {isFR ? 'Aujourd’hui' : 'Today'}
              </p>
              <p className="font-sans text-[16px] font-semibold text-[#121212] leading-snug mt-1" data-id="element-4528" data-mp="2q81ojl6xriuy">
                {isFR ? 'Des systèmes multiples, des opérations fragmentées.' : 'Multiple systems, fragmented operations.'}
              </p>
              <ul className="mt-4 flex flex-wrap gap-2" data-id="element-4529" data-mp="3dw2pa7xvhuzt">
                {LEGACY.map(name => <li key={name} className="rounded-lg border border-[#E6E8EE] bg-white px-2.5 py-1.5 font-sans text-[12.5px] font-semibold text-[#6B7280]" data-id="element-4530" data-mp="38vwwgofva2g5">
                    {name}
                  </li>)}
                <li className="rounded-lg border border-[#E6E8EE] bg-white px-2.5 py-1.5 font-sans text-[12.5px] text-[#6B7280]" data-id="element-4531" data-mp="2z5vg6itr9obs">
                  {isFR ? 'Autres logiciels' : 'Other software'}
                </li>
              </ul>
              <ul className="mt-4 space-y-2" data-id="element-4532" data-mp="3rt7vc7xl6tv2">
                {BEFORE.map(b => <li key={b.en} className="flex items-center gap-2.5 font-sans text-[13.5px] text-[#3B4252]" data-id="element-4533" data-mp="1pyk22eir3py6">
                    <span className="w-[18px] h-[18px] rounded-full bg-[#FDE2DF] flex items-center justify-center shrink-0" data-id="element-4534" data-mp="1uyy4nh1l27ot">
                      <XIcon className="w-2.5 h-2.5 text-[#D93036]" strokeWidth={3} aria-hidden="true" data-id="element-4535" data-mp="1l6sd28f8tsa5" />
                    </span>
                    {pick(b)}
                  </li>)}
              </ul>
            </div>

            <div className="flex md:flex-col items-center justify-center gap-2 px-1" data-id="element-4536" data-mp="1b9nndym6veol">
              <span className="font-sans text-[12.5px] font-medium text-[#121212] text-center leading-tight" data-id="element-4537" data-mp="xa4rc46njtsn">
                {isFR ? <>Migration<br className="hidden md:block" data-id="element-4538" data-mp="12a0uy2bftj7o" /> accompagnée</> : <>Guided<br className="hidden md:block" data-id="element-4539" data-mp="2rlb46t3jinx7" /> migration</>}
              </span>
              <span className="flex items-center" aria-hidden="true" data-id="element-4540" data-mp="2wjusv19iqdt9">
                <span className="w-10 border-t-2 border-dashed border-[#A5A8E6] md:block" data-id="element-4541" data-mp="kzddve0tteee" />
                <ArrowRightIcon className="w-5 h-5 -ml-1 text-[#4F46E5] rotate-90 md:rotate-0" data-id="element-4542" data-mp="g13mzfm4wzyl" />
              </span>
            </div>

            <div className="rounded-2xl border border-[#E4E3FA] bg-[#F7F7FE] p-5" data-id="element-4543" data-mp="uqhvymlstfdg">
              <p className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#4F46E5]" data-id="element-4544" data-mp="pwnabdmmuqbq">
                {isFR ? 'Demain' : 'Tomorrow'}
              </p>
              <p className="font-sans text-[16px] font-semibold text-[#121212] leading-snug mt-1" data-id="element-4545" data-mp="ypgxdx8bmu05">
                {isFR ? 'Une seule plateforme intelligente.' : 'One intelligent platform.'}
              </p>
              <p className="mt-4 flex items-center gap-2.5" data-id="element-4546" data-mp="18ozc9z9djplc">
                <WavesIcon className="w-7 h-7 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4547" data-mp="3fedzv84uvusc" />
                <span className="font-sans text-[22px] font-semibold tracking-[-0.02em] text-[#121212]" data-id="element-4548" data-mp="3agcf8irs7ner">{isFR ? 'Loisirs ATNA' : 'ATNA Recreation'}</span>
              </p>
              <ul className="mt-4 space-y-2" data-id="element-4549" data-mp="2oksx1rn64542">
                {AFTER.map(a => <li key={a.en} className="flex items-center gap-2.5 font-sans text-[13.5px] text-[#3B4252]" data-id="element-4550" data-mp="2tjnf2rdb9pn4">
                    <span className="w-[18px] h-[18px] rounded-full bg-[#22A55B] flex items-center justify-center shrink-0" data-id="element-4551" data-mp="2yet43z20fkff">
                      <CheckIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} aria-hidden="true" data-id="element-4552" data-mp="25oaz2shq78zd" />
                    </span>
                    {pick(a)}
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Right */}
        <motion.div {...reveal} transition={{
        duration: 0.5,
        delay: 0.08,
        ease: EASE
      }} className="relative" data-id="element-4553" data-mp="2jo296i54g583">
          <div className="hidden md:block xl:pr-0" data-id="element-4554" data-mp="3r1vxkcrtx4yt">
            <LoisirsOpsDashboard data-id="element-4555" data-mp="3r36ze71ps2na" />
          </div>
          <div className="md:-mt-[300px] md:ml-auto md:mr-6 md:w-[min(560px,92%)] relative z-10" data-id="element-4556" data-mp="3v1y00o5e22f5">
            <LoisirsDemoCard data-id="element-4557" data-mp="1jfm37vgq1r0f" />
          </div>
        </motion.div>
      </div>

      {/* Benefits */}
      <motion.ul {...reveal} transition={{
      duration: 0.5,
      ease: EASE
    }} className="mt-14 rounded-3xl bg-[#F7F7FC] px-6 py-7 md:px-10 grid grid-cols-1 md:grid-cols-3 md:divide-x divide-[#E3E4EE] gap-6 md:gap-0" data-id="element-4558" data-mp="1fdird5q54kem">
        {BENEFITS.map(({
        icon: Icon,
        lead,
        accent
      }) => <li key={lead.en} className="flex items-center gap-5 md:px-8 first:md:pl-0 last:md:pr-0" data-id="element-4559" data-mp="10pxlj41vs730">
            <span className="shrink-0 w-16 h-16 rounded-full bg-[#ECECFB] flex items-center justify-center" data-id="element-4560" data-mp="voqdd3vtt5vt">
              <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4561" data-mp="rozh4knj5xjw" />
            </span>
            <p className="font-sans text-xl md:text-[22px] font-semibold tracking-[-0.02em] text-[#121212] leading-snug" data-id="element-4562" data-mp="mqoa0home15d">
              {pick(lead)} <span className="text-[#4F46E5]" data-id="element-4563" data-mp="2h34jdrcppveg">{pick(accent)}</span>
            </p>
          </li>)}
      </motion.ul>
    </div>;
}